import { Client } from './client.model';
import { PhoneConfigEntry } from '../shared/models/phone-config.model';

/**
 * Some clients only have `client_ids.uid` populated (newer records); others only have the
 * legacy `client_id`. Both fields are optional on `Client`, so callers must check both —
 * reading either one alone silently returns null for the clients that only have the other.
 * `client.id` (the Firestore doc id) is the final fallback: `saveNewClient` always sets both
 * `client_id` and `client_ids.uid` from it, so any client loaded from Firestore carries it.
 *
 * Uses `||`, not `??`: an empty string must fall through too, not just null/undefined — the
 * backend's DTO for this id hard-rejects `''` with a 400, while `null` takes a defined "no
 * filter" path, so treating them the same here is strictly safer.
 */
export const getClientId = (client?: Client | null): string | null =>
  client?.client_ids?.uid || client?.client_id || client?.id || null;

/**
 * Sentinel id for the entry synthesized from a legacy `phoneConfig` object.
 *
 * ## Why a synthesized id exists at all
 *
 * The three call/SMS readers do not go through the API — they read `clients/{id}` straight out of
 * Firestore, where an unmigrated client has only the `phoneConfig` object, and that object has no
 * `id` field of any kind. `PhoneConfigEntry.id` is required, so something has to go there.
 *
 * The backend derives a deterministic id for the same entry (a truncated sha256 of the number) when
 * it hydrates it for `GET /phones`. This sentinel is deliberately **not** that: reproducing the
 * derivation here would put one id algorithm in two repositories, which is more fragile than a value
 * that is obviously not a server id.
 *
 * It identifies no real entry, so it must never be sent back to the API as an entry id. The
 * per-patient preferred-line endpoints that were the one way to do so are gone, but the sentinel
 * still leaves the frontend only as a value the backend would not recognise.
 */
export const LEGACY_PHONE_CONFIG_ID = '__legacy_phone_config__';

/**
 * Resolves a client's outbound lines to a single shape, whatever the stored format is.
 *
 * `phoneConfigs` (a list) is the source of truth as of NP-04; the single `phoneConfig` object
 * is kept only as a mirror of the primary entry, for readers that predate the list. A client
 * that has not been migrated yet has only the object, so reading `phoneConfigs` alone would
 * report "no line configured" for a client that has a perfectly good one — hence the fallback.
 *
 * The fallback fills the field the object format simply does not carry: `region` becomes null —
 * unknown, NOT toll-free. The caller cannot distinguish the two here, which is why the toll-free
 * warning belongs on real entries only.
 *
 * Returns entries in stored order, which is insertion order — the array is append-only, and
 * both the primary-entry mirror rule and Step 2's tie-break depend on that order.
 */
export const getClientPhoneConfigs = (client?: Client | null): PhoneConfigEntry[] => {
  if (client?.phoneConfigs?.length) {
    return client.phoneConfigs;
  }

  const legacy = client?.phoneConfig;
  if (!legacy?.phoneNumber) {
    return [];
  }

  return [
    {
      id: LEGACY_PHONE_CONFIG_ID,
      phoneNumber: legacy.phoneNumber,
      // Absent flag means inactive, matching both the readers this replaced — they tested
      // `phoneConfig?.isActive`, which is falsy when the field is missing — and the backend's own
      // hydration of the same object.
      //
      // The strong reason is inbound routing, not consistency. The legacy reverse lookup filters on
      // `phoneConfig.isActive == true`, and Firestore excludes documents that lack the field being
      // filtered, so a legacy number with no flag has never received an inbound call. Defaulting it
      // to active here would have it backfilled into the active-numbers index and *start* routing
      // calls that do not arrive today: a clinic taking patient calls on a number the system never
      // assigned it, with nothing anywhere reporting an error.
      isActive: legacy.isActive ?? false,
      provider: legacy.provider ?? 'twilio',
      region: null,
      isoCountry: null,
    },
  ];
};

/** The lines a staff member may actually send from. Same fallback rules as `getClientPhoneConfigs`. */
export const getActiveClientPhoneConfigs = (client?: Client | null): PhoneConfigEntry[] =>
  getClientPhoneConfigs(client).filter((entry) => entry.isActive);

/**
 * Normalizes what the admin typed into the value stored on `Client.client_stedi_npi`.
 *
 * Create and edit reach Firestore through entirely different paths — `buildClient()` writes with
 * the client SDK, the dialog sends a `PUT` to the core clients endpoint — so the rule lives here
 * rather than being spelled out twice and left to drift.
 *
 * Trims first, so a whitespace-only entry stores `null` instead of blanks that would read as a
 * present value everywhere downstream. The `null` branch is defensive: `Validators.required` on
 * the form means an empty value cannot currently be submitted from the UI.
 */
export const normalizeStediNpi = (value?: string | null): string | null => (value ?? '').trim() || null;
